export type POBManifestStatus = 'draft' | 'published' | 'archived';
export type POBEntryStatus = 'on_board' | 'departed' | 'planned';
export type SSEContext =
  | 'new_to_industry'
  | 'new_to_role'
  | 'new_to_company'
  | 'returned_from_leave';

export interface POBManifest {
  id: string;
  tenant_id: string;
  project_id: string;
  manifest_date: string; // YYYY-MM-DD
  facility_name: string | null;
  total_pob: number | null;
  status: POBManifestStatus;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  is_deleted: boolean;
  entries?: POBEntry[];
  projects?: { name: string; code: string | null } | null;
}

export interface POBEntry {
  id: string;
  tenant_id: string;
  manifest_id: string;
  project_id: string;
  first_name: string;
  last_name: string;
  company: string;
  position_title: string;
  hitch_on_date: string;
  hitch_off_date: string | null;
  // Manually designated — true if person has < 6 months experience
  // in current craft/position. NOT auto-computed from hitch dates.
  is_sse: boolean;
  sse_context: SSEContext | null;
  sse_notes: string | null;
  status: POBEntryStatus;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  is_deleted: boolean;
}

export type ColumnMappingModule = 'pob' | 'activities' | 'permits';

export interface POBColumnMapping {
  id: string;
  tenant_id: string;
  mapping_name: string;
  column_map: Record<string, string>;
  is_default: boolean;
  module: ColumnMappingModule;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface POBEntryInput {
  first_name: string;
  last_name: string;
  company: string;
  position_title: string;
  hitch_on_date: string;
  hitch_off_date?: string | null;
  status: POBEntryStatus;
  // Manually designated — true if person has < 6 months experience
  // in current craft/position. NOT auto-computed from hitch dates.
  is_sse: boolean;
  sse_context?: SSEContext | null;
  sse_notes?: string | null;
}

export const SSE_CONTEXT_LABELS: Record<SSEContext, string> = {
  new_to_industry: 'New to Industry',
  new_to_role: 'New to Role',
  new_to_company: 'New Company',
  returned_from_leave: 'From Leave',
};

// Tailwind utility classes for SSE context badges. Distinct colors —
// SSE context is safety-critical, must be visually unambiguous.
export const SSE_CONTEXT_BADGE: Record<SSEContext, string> = {
  new_to_industry: 'bg-red-100 text-red-700 border-red-300',
  new_to_role: 'bg-amber-100 text-amber-800 border-amber-300',
  new_to_company: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  returned_from_leave: 'bg-blue-100 text-blue-700 border-blue-300',
};

